import { useNavigationStore } from "@/stores/navigation-store";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
    MessageCirclePlus,
    Search,
    Settings2,
    Keyboard,
    HelpCircle,
    Bot,
    Mail,
    Bug,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";

const HelpDialog = () => {
    const { helpIsOpen, setHelpIsOpen } = useNavigationStore();
    const { t } = useTranslation();

    // Fetch user's enabled agent connections
    const { data: userConnections, isLoading: loadingConnections } = trpc.userGroups.getMyAgentConnections.useQuery(
        undefined,
        { enabled: helpIsOpen }
    );

    const shortcuts = [
        { key: "Ctrl + K", description: t("help.shortcuts.openSearch"), icon: Search },
        { key: "Ctrl + Alt + N", description: t("help.shortcuts.startNewChat"), icon: MessageCirclePlus },
        { key: "F9", description: t("help.shortcuts.reportBug"), icon: Bug },
        { key: "?", description: t("help.shortcuts.openHelp"), icon: HelpCircle },
    ];

    const features = [
        {
            title: t("help.features.newChat.title"),
            icon: MessageCirclePlus,
        },
        {
            title: t("help.features.search.title"),
            icon: Search,
        },
        {
            title: t("help.features.settings.title"),
            icon: Settings2,
        },
    ];

    return (
        <Dialog onOpenChange={(open) => setHelpIsOpen(open)} open={helpIsOpen}>
            <DialogContent className="max-h-[80vh] max-w-sm md:max-w-lg 4xl:max-w-xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <HelpCircle className="h-5 w-5" />
                        Help
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Features */}
                    <section>
                        <h3 className="mb-3 text-lg font-semibold">{t("help.featuresTitle")}</h3>
                        <div className="grid gap-2">
                            {features.map((feature, index) => (
                                <div key={index} className="flex items-center gap-3 rounded-lg border bg-card p-3">
                                    <feature.icon className="h-4 w-4 text-primary" />
                                    <span className="font-medium">{feature.title}</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    <Separator />

                    {/* Keyboard Shortcuts */}
                    <section>
                        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
                            <Keyboard className="h-4 w-4" />
                            {t("help.keyboardShortcuts")}
                        </h3>
                        <div className="grid gap-2">
                            {shortcuts.map((shortcut, index) => (
                                <div key={index} className="flex items-center justify-between py-2">
                                    <div className="flex items-center gap-2">
                                        <shortcut.icon className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm">{shortcut.description}</span>
                                    </div>
                                    <Badge variant="outline" className="font-mono text-xs">
                                        {shortcut.key}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    </section>

                    <Separator />

                    {/* Available Agents */}
                    <section>
                        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
                            <Bot className="h-4 w-4" />
                            Your Enabled Agents
                        </h3>
                        {loadingConnections ? (
                            <p className="text-sm text-muted-foreground">Loading...</p>
                        ) : userConnections && userConnections.length > 0 ? (
                            <div className="space-y-2">
                                {userConnections.map((connection: any) => (
                                    <div
                                        key={connection.id}
                                        className="flex items-center justify-between rounded-lg border bg-card p-3"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium">{connection.name}</div>
                                            {connection.description && (
                                                <div className="text-xs text-muted-foreground">
                                                    {connection.description}
                                                </div>
                                            )}
                                        </div>
                                        <Badge variant={connection.isDefault ? "default" : "secondary"} className="ml-2">
                                            {connection.type.charAt(0).toUpperCase() + connection.type.slice(1)}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No agents currently enabled. Check AI Configuration → Connectors to view and manage
                                available agents.
                            </p>
                        )}
                    </section>

                    <Separator />

                    {/* Contact/Support */}
                    <section>
                        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
                            <Mail className="h-4 w-4" />
                            Contact & Support
                        </h3>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                <span className="text-sm">Ask the AI assistant directly</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                <span className="text-sm">Check Settings for configuration options</span>
                            </div>
                        </div>
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default HelpDialog;
